import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, Landmark, Calendar, Package, AlertCircle } from "lucide-react";

/**
 * DashboardAlerts - shows critical alerts:
 *  - Cheques due within 7 days + overdue
 *  - Products expiring within 30 days + already expired
 *  - Low stock items
 */
export default function DashboardAlerts() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/alerts").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const c = data.counts || {};
  const total = (c.cheques_soon || 0) + (c.cheques_overdue || 0) + (c.expiring || 0) + (c.expired || 0) + (c.low_stock || 0) + (c.pending_users || 0);
  if (total === 0) return null;

  return (
    <div className="border-2 border-amber-200 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50/40 p-4" data-testid="dashboard-alerts">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <h2 className="font-heading font-bold text-lg">تنبيهات تحتاج انتباهك ({total})</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {c.cheques_overdue > 0 && (
          <Link to="/cheques" className="p-3 rounded-lg bg-white border-2 border-red-300 hover:border-red-500 transition group" data-testid="alert-cheques-overdue">
            <div className="flex items-center gap-2 text-red-700"><Landmark className="w-4 h-4" /><span className="text-xs font-bold">شيكات متأخرة</span></div>
            <div className="text-2xl font-bold text-red-700 mt-1 font-mono">{c.cheques_overdue}</div>
          </Link>
        )}
        {c.cheques_soon > 0 && (
          <Link to="/cheques" className="p-3 rounded-lg bg-white border-2 border-amber-300 hover:border-amber-500 transition" data-testid="alert-cheques-soon">
            <div className="flex items-center gap-2 text-amber-700"><Landmark className="w-4 h-4" /><span className="text-xs font-bold">شيكات قريبة</span></div>
            <div className="text-2xl font-bold text-amber-700 mt-1 font-mono">{c.cheques_soon}</div>
            <div className="text-[10px] text-muted-foreground">خلال 7 أيام</div>
          </Link>
        )}
        {c.expired > 0 && (
          <Link to="/products" className="p-3 rounded-lg bg-white border-2 border-red-300 hover:border-red-500 transition" data-testid="alert-expired">
            <div className="flex items-center gap-2 text-red-700"><Calendar className="w-4 h-4" /><span className="text-xs font-bold">منتهية الصلاحية</span></div>
            <div className="text-2xl font-bold text-red-700 mt-1 font-mono">{c.expired}</div>
            <div className="text-[10px] text-muted-foreground">مع مخزون!</div>
          </Link>
        )}
        {c.expiring > 0 && (
          <Link to="/products" className="p-3 rounded-lg bg-white border-2 border-orange-300 hover:border-orange-500 transition" data-testid="alert-expiring">
            <div className="flex items-center gap-2 text-orange-700"><Calendar className="w-4 h-4" /><span className="text-xs font-bold">تقارب الانتهاء</span></div>
            <div className="text-2xl font-bold text-orange-700 mt-1 font-mono">{c.expiring}</div>
            <div className="text-[10px] text-muted-foreground">خلال 30 يوم</div>
          </Link>
        )}
        {c.low_stock > 0 && (
          <Link to="/products" className="p-3 rounded-lg bg-white border-2 border-blue-300 hover:border-blue-500 transition" data-testid="alert-low-stock">
            <div className="flex items-center gap-2 text-blue-700"><Package className="w-4 h-4" /><span className="text-xs font-bold">مخزون منخفض</span></div>
            <div className="text-2xl font-bold text-blue-700 mt-1 font-mono">{c.low_stock}</div>
          </Link>
        )}
        {c.pending_users > 0 && (
          <Link to="/pending-users" className="p-3 rounded-lg bg-white border-2 border-primary hover:bg-primary/5 transition" data-testid="alert-pending-users">
            <div className="flex items-center gap-2 text-primary"><Package className="w-4 h-4" /><span className="text-xs font-bold">مستخدمون جدد</span></div>
            <div className="text-2xl font-bold text-primary mt-1 font-mono">{c.pending_users}</div>
            <div className="text-[10px] text-muted-foreground">بانتظار التفعيل</div>
          </Link>
        )}
      </div>

      {/* Detailed lists (top 3 of each) */}
      {(data.cheques_overdue?.length > 0 || data.cheques_due_soon?.length > 0) && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <div className="text-xs font-bold text-muted-foreground mb-1">أقرب الشيكات:</div>
          <div className="space-y-1">
            {[...(data.cheques_overdue || []), ...(data.cheques_due_soon || [])].slice(0, 3).map(ch => (
              <div key={ch.id} className="flex items-center justify-between text-xs bg-white/70 rounded px-2 py-1">
                <div className="flex items-center gap-2">
                  <AlertCircle className={`w-3 h-3 ${ch.due_date < new Date().toISOString().slice(0, 10) ? "text-red-600" : "text-amber-600"}`} />
                  <span>{ch.kind === "received" ? "من" : "إلى"} <b>{ch.party_name}</b> · <span className="font-mono">{(ch.amount || 0).toLocaleString("ar-EG")} {ch.currency}</span></span>
                </div>
                <span className="font-mono text-muted-foreground">{ch.due_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
